import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Operation, StageStatus } from '@iotv/io';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { Alert, Box, Button, Card, CardContent, FormLabel, keyframes, LinearProgress, linearProgressClasses, Stack, Tab, Tabs, tabsClasses, Typography, useTheme, } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { AppMainViewContext } from '../../ContextProviders/AppContextProvider';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { subscribeToIOJob, subscribeToIOQueue } from '../../data/daoFunctions/daoFunctions';
import { useGoogleSheetSelectors } from '../../Google/Sheets/UseGoogleSheetSelector';
import { AnimatedTimeline, stringToId } from '../common/AnimatedTimeline';
import { useExportControls } from './useExportControls';
const { REACT_APP_DEBUG } = process.env;
const debug = REACT_APP_DEBUG && false;
/** https://amitd.co/code/javascript/scroll-into-view-with-offset */
const scrollIntoView = (selector) => {
    document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
};
const slideAndFade = keyframes `
  0% {
    opacity: 1;
    background-position-x: 0;
  }
  70% {
    opacity: 0;
    background-position-x: -15em;
  }
  90 % {
    opacity: 0;
    background-position-x: -15em;
  }
  100% {
    opacity: 1;
    background-position-x: -15em;
  }
`;
export const ImportExport = (props) => {
    const { user } = props;
    const theme = useTheme();
    const [{ isSmallView }] = useContext(AppMainViewContext);
    // current job state
    const [stages, setStages] = useState(null);
    const [errorToThrow, setErrorToThrow] = useState();
    const [error, setError] = useState(false);
    const [scheduledProgress, setScheduledProgress] = useState(0);
    const [currentProgress, setCurrentProgress] = useState(0);
    // queue state
    const [currentJob, setCurrentJob] = useState(null);
    const [queue, setQueue] = useState(null);
    const [queueSubscribed, setQueueSubscribed] = useState(false);
    const [jobSubscribed, setJobSubscribed] = useState(false);
    const [locked, setLocked] = useState(true);
    // local job state
    const [operation, setOperation] = useState(Operation.IMPORT);
    const { components, state } = useGoogleSheetSelectors({ disabled: locked, required: true });
    const [missing, setMissing] = useState(true);
    // UI hooks
    const { controls: exportControls, objectMode: exportObjectMode, setObjectMode: setExportObjectMode, exportObjects, setExportObjects, } = useExportControls({ disabled: locked });
    // subscribe to MQTT
    useEffect(() => {
        if (user?.id) {
            ConsumerDao.io.status().then(({ err, data }) => {
                debug && console.log('Import/Export HTTP Status', err, data);
                if (err) {
                    const error = new Error(err.message);
                    if (err.message === 'The specified queue does not exist.')
                        error.message += " Please deploy 'SQS'!";
                    error.name = 'AWS IoT / PubSub (MQTT)';
                    setErrorToThrow(error);
                }
                if (err || !data)
                    return;
                debug && console.log('Import/Export HTTP Queue status', data);
                setQueue(data.queue);
            });
            if (!queueSubscribed) {
                const subscription = subscribeToIOQueue((data) => {
                    debug && console.log('Import/Export MQTT queue', data.value);
                    setQueue(data.value.queue);
                    setCurrentJob(data.value.currentJob ?? null);
                });
                setQueueSubscribed(subscription ? true : false);
            }
            if (!jobSubscribed) {
                const subscription = subscribeToIOJob(user, (data) => {
                    debug && console.log('Import/Export MQTT job', data.value);
                    if (currentJob?.user !== user.id || (currentJob?.user === user.id && data.value.stages?.[0]?.status !== StageStatus.UNSTARTED)) {
                        setStages(data.value.stages);
                        setScheduledProgress(data.value.progress.scheduled);
                        setCurrentProgress(data.value.progress.current);
                    }
                });
                setJobSubscribed(subscription ? true : false);
            }
        }
    }, [user?.id]);
    useEffect(() => {
        debug && console.log('Import/Export used effect on currentJob', currentJob);
        // if there is no current job, bail
        if (currentJob === null)
            return;
        // if the current job doesn't match the logged-in user, bail
        if (currentJob.user !== user?.id)
            return;
        // if there is a selected file, bail
        if (state.selectedFile !== null)
            return;
        setOperation(currentJob.operation);
        state.setSelectedFile({ id: currentJob.spreadsheetID, name: currentJob.spreadsheetName });
        switch (currentJob.operation) {
            case Operation.IMPORT: {
                break;
            }
            case Operation.EXPORT: {
                setExportObjectMode(currentJob.objectMode);
                setExportObjects(currentJob.objects);
                break;
            }
        }
    }, [currentJob]);
    useEffect(() => {
        if (user) {
            const noQueue = queue === null;
            const noStages = stages === null;
            const inQueue = queue?.map((j) => j.user).includes(user.id);
            const isProcessing = stages ? stages[0]?.status !== StageStatus.UNSTARTED && stages[stages.length - 1]?.status !== StageStatus.COMPLETED : false;
            const noError = !stages?.map((s) => s.status).includes(StageStatus.ERRORED);
            setLocked(noQueue || noStages || inQueue || (isProcessing && noError));
        }
    }, [queue, stages]);
    useEffect(() => {
        const noSpreadsheet = state.selectedFile === null;
        switch (operation) {
            case Operation.IMPORT: {
                setMissing(noSpreadsheet);
                return;
            }
            case Operation.EXPORT: {
                const noObjects = exportObjects.length === 0;
                setMissing(noSpreadsheet || noObjects);
                break;
            }
        }
    }, [state]);
    useEffect(() => {
        if (stages?.length) {
            let index = 0;
            const unstartedIndex = stages.findIndex((stage) => stage.status === StageStatus.UNSTARTED);
            const inprogressIndex = stages.findIndex((stage) => stage.status === StageStatus.INPROGRESS);
            const completedIndex = stages.findLastIndex((stage) => stage.status === StageStatus.COMPLETED);
            index = Math.max(index, Math.min(unstartedIndex, inprogressIndex), completedIndex);
            const stageId = stages[index]?.id;
            if (stages[0]?.status !== StageStatus.UNSTARTED)
                scrollIntoView(`#${stringToId(stageId)}`);
            setError(stages.map((s) => s.status).includes(StageStatus.ERRORED));
        }
        else {
            setError(false);
        }
    }, [stages]);
    const submit = async () => {
        setLocked(true);
        setScheduledProgress(0);
        setCurrentProgress(0);
        switch (operation) {
            case Operation.IMPORT: {
                const importRes = await ConsumerDao.io.import({
                    operation,
                    spreadsheetID: state.selectedFile?.id ?? '',
                    spreadsheetName: state.selectedFile?.name ?? '',
                    submitted: Date.now(),
                });
                debug && console.log('Import/Export HTTP Import', importRes.err, importRes.data);
                if (!importRes.err && importRes.data)
                    setLocked(importRes.data.enqueued);
                break;
            }
            case Operation.EXPORT: {
                const exportRes = await ConsumerDao.io.export({
                    operation,
                    spreadsheetID: state.selectedFile?.id ?? '',
                    spreadsheetName: state.selectedFile?.name ?? '',
                    objectMode: exportObjectMode,
                    objects: exportObjects,
                    submitted: Date.now(),
                });
                debug && console.log('Import/Export HTTP Export', exportRes.err, exportRes.data);
                if (!exportRes.err && exportRes.data)
                    setLocked(exportRes.data.enqueued);
                break;
            }
        }
    };
    if (errorToThrow)
        throw errorToThrow;
    return (
    // unsetting overflow (on the container) is required to make `position: sticky` work
    _jsxs(Stack, { gap: 1, sx: { overflow: 'unset' }, children: [_jsx(Typography, { variant: "h4", children: "Import / Export" }), _jsx(Stack, { direction: isSmallView ? 'column' : 'row', children: _jsxs(Alert, { severity: "info", children: ["Jobs in queue: ", queue?.length ?? '...', user && queue?.map((j) => j.user).includes(user.id) ? ', including yours' : ''] }) }), _jsx(FormLabel, { children: "Operation" }), _jsxs(Tabs, { value: operation, onChange: (_, v) => setOperation(v), variant: isSmallView ? 'fullWidth' : 'standard', sx: { minHeight: 0, [`& .${tabsClasses.list}`]: { gap: 1 } }, slotProps: { indicator: { sx: { display: 'none' } } }, children: [_jsx(Tab, { label: Operation.IMPORT, value: Operation.IMPORT, disabled: locked, icon: _jsx(DownloadIcon, {}), iconPosition: "start", sx: { minHeight: 0, backgroundColor: null, borderRadius: 1, '&.Mui-selected': { backgroundColor: theme.palette.action.selected } } }), _jsx(Tab, { label: Operation.EXPORT, value: Operation.EXPORT, disabled: locked, icon: _jsx(UploadIcon, {}), iconPosition: "start", sx: { minHeight: 0, backgroundColor: null, borderRadius: 1, '&.Mui-selected': { backgroundColor: theme.palette.action.selected } } })] }), _jsx(FormLabel, { children: "Google Sheet" }), _jsxs(Stack, { direction: "row", gap: 1, children: [components.SpreadsheetSelector, components.SpeadsheetUrlLink, state.error && (_jsx(Alert, { severity: "error", sx: { paddingBlock: 0, alignItems: 'center' }, children: state.error }))] }), operation === Operation.EXPORT && exportControls, _jsx(Stack, { direction: "row", children: _jsx(AnimatedTimeline, { stages: stages ?? [] }) }), _jsx(Stack, { direction: "row", sx: { position: 'sticky', bottom: '1em', containerType: 'inline-size', width: '100%' }, children: _jsx(Card, { elevation: 3, sx: { borderRadius: '2em', paddingBottom: 0 }, children: _jsx(CardContent, { sx: {
                            minWidth: queue === null || stages === null || !locked ? '0cqw' : 'calc(100cqw - 2em)',
                            padding: '1em !important',
                            transition: 'min-width 2s ease-in-out',
                        }, children: _jsxs(Box, { sx: { display: 'grid', justifyItems: 'start', alignItems: 'center', borderRadius: '1em', overflow: 'hidden' }, children: [_jsx(Button, { variant: "contained", onClick: submit, disabled: missing || locked, sx: {
                                        gridArea: '1 / 1 / 1 / 1',
                                        zIndex: 1,
                                        width: 'fit-content',
                                        paddingTop: 0,
                                        paddingBottom: 0,
                                        borderRadius: '1em',
                                        fontSize: '1em',
                                        transition: 'opacity 2s ease-in-out',
                                        opacity: queue === null || stages === null || !locked ? 1 : 0,
                                    }, children: "START" }), stages && stages.length && (_jsx(LinearProgress, { variant: "buffer", value: currentProgress, valueBuffer: scheduledProgress, color: error ? 'error' : currentProgress === 100 ? 'success' : 'primary', sx: {
                                        gridArea: '1 / 1 / 1 / 1',
                                        height: '1.5em',
                                        width: queue === null || stages === null || !locked ? '0cqw' : 'calc(100cqw - 2em)',
                                        borderRadius: '1em',
                                        transition: 'width 2s ease-in-out',
                                        [`& .${linearProgressClasses.dashed}`]: {
                                            backgroundSize: '1.5em 1.5em',
                                            backgroundRepeat: 'repeat no-repeat',
                                            backgroundPositionY: 0,
                                            animation: `${slideAndFade} 4s ease-in-out infinite`,
                                            backgroundImage: (theme) => `radial-gradient(circle closest-side at center, color-mix(in oklab, ${error ? theme.palette.error.main : theme.palette.primary.main} 25%, transparent 75%) 60%, transparent 60%)`,
                                        },
                                        [`& .${linearProgressClasses.bar}`]: {
                                            borderRadius: '1em',
                                            transitionProperty: 'background-color, transform',
                                            transitionDuration: '0.5s',
                                            transitionTimingFunction: 'ease-in-out',
                                        },
                                    } }))] }) }) }) })] }));
};
